import { useRef } from 'react'
import { useParams } from 'react-router-dom'

import type { ErrorLike } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import { useCommentData } from '../../Comments/context/CommentContext'
import { useCommentsTracking } from '../tracking/useCommentsTracking'
import { useDismissalTracker } from '../tracking/useDismissalTracker'

export const DismissCommentMutationGQL = graphql(/* GraphQL */ `
  mutation DismissComment($input: DismissCommentInput!) {
    dismissComment(input: $input) {
      __typename
      ... on DismissCommentSuccess {
        comment {
          ... on BaseComment {
            __typename
            id
            status
            text
            updatedAt
            parentId
          }
          ... on CommentFromThirdParty {
            labels
          }
          ... on CommentFromBuffer {
            publishingStatus {
              status
              errorMessage
            }
          }
        }
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export type DismissCommentMutation = ResultOf<typeof DismissCommentMutationGQL>

type UseDismissCommentResult = {
  dismissComment: (commentId: string) => Promise<boolean>
  loading: boolean
}

export const useDismissComment = (): UseDismissCommentResult => {
  const comment = useCommentData()
  const { channelId } = useParams<{ channelId: string }>()
  const { onDismiss } = useDismissalTracker(channelId ? [channelId] : [])
  const { trackCommentReplyError } = useCommentsTracking()
  const latestApolloErrorRef = useRef<ErrorLike | null>(null)

  const [mutate, { loading }] = useTypedMutation(
    DismissCommentMutationGQL,
    (data) => data.dismissComment,
    {
      successTypename: 'DismissCommentSuccess',
      fetchPolicy: 'no-cache',
      onError: (error) => {
        latestApolloErrorRef.current = error
      },
    },
  )

  const dismissComment = async (commentId: string): Promise<boolean> => {
    latestApolloErrorRef.current = null

    const dismissStartTime = Date.now()
    const result = await mutate({
      variables: {
        input: {
          commentId,
        },
      },
    })

    if (result.success) {
      onDismiss()
      toast.success('Comment resolved successfully', {
        duration: 1_000,
      })
      return true
    }

    trackCommentReplyError({
      apolloError: latestApolloErrorRef.current ?? new Error('Unknown error'),
      comment,
      text: '',
      source: 'frontend:dismiss',
      elapsedMs: Date.now() - dismissStartTime,
    })

    return false
  }

  return {
    dismissComment,
    loading,
  }
}
